import api from './api'

/**
 * Property Maps Service
 * Quản lý maps với markers, sharing, analytics
 */
const mapService = {
  // ============================================================
  // MAPS CRUD
  // ============================================================

  /**
   * Lấy danh sách maps của user
   * @param {Object} params - { page, per_page, status }
   */
  async getMaps(params = {}) {
    const response = await api.get('/maps', { params })
    return response.data
  },

  /**
   * Lấy chi tiết 1 map (bao gồm markers)
   * @param {number} mapId
   */
  async getMap(mapId) {
    const response = await api.get(`/maps/${mapId}`)
    return response.data
  },

  /**
   * Tạo map mới
   * @param {Object} data - { name, description, center_lat, center_lng, ... }
   */
  async createMap(data) {
    const response = await api.post('/maps', data)
    return response.data
  },

  /**
   * Cập nhật map
   * @param {number} mapId
   * @param {Object} data
   */
  async updateMap(mapId, data) {
    const response = await api.put(`/maps/${mapId}`, data)
    return response.data
  },

  /**
   * Xóa map (archive)
   * @param {number} mapId
   */
  async deleteMap(mapId) {
    const response = await api.delete(`/maps/${mapId}`)
    return response.data
  },

  /**
   * Xuất bản map
   * @param {number} mapId
   */
  async publishMap(mapId) {
    const response = await api.post(`/maps/${mapId}/publish`)
    return response.data
  },

  /**
   * Hủy xuất bản (chuyển về draft)
   * @param {number} mapId
   */
  async unpublishMap(mapId) {
    const response = await api.delete(`/maps/${mapId}/publish`)
    return response.data
  },

  // ============================================================
  // MARKERS CRUD
  // ============================================================

  /**
   * Lấy markers của map
   * @param {number} mapId
   * @param {string} type - 'all', 'ban', 'thue'
   */
  async getMarkers(mapId, type = 'all') {
    const response = await api.get(`/maps/${mapId}/markers`, {
      params: { type },
    })
    return response.data
  },

  /**
   * Thêm marker vào map
   * @param {number} mapId
   * @param {Object} data - { name, latitude, longitude, marker_type, price_text, ... }
   */
  async addMarker(mapId, data) {
    const response = await api.post(`/maps/${mapId}/markers`, data)
    return response.data
  },

  /**
   * Cập nhật marker
   * @param {number} mapId
   * @param {number} markerId
   * @param {Object} data
   */
  async updateMarker(mapId, markerId, data) {
    const response = await api.put(`/maps/${mapId}/markers/${markerId}`, data)
    return response.data
  },

  /**
   * Xóa marker
   * @param {number} mapId
   * @param {number} markerId
   */
  async deleteMarker(mapId, markerId) {
    const response = await api.delete(`/maps/${mapId}/markers/${markerId}`)
    return response.data
  },

  /**
   * Thêm nhiều markers cùng lúc
   * @param {number} mapId
   * @param {Array} markers
   */
  async addMarkersBulk(mapId, markers) {
    const response = await api.post(`/maps/${mapId}/markers/bulk`, { markers })
    return response.data
  },

  /**
   * Sắp xếp lại thứ tự markers
   * @param {number} mapId
   * @param {Array} order - [{ id, order }, ...]
   */
  async reorderMarkers(mapId, order) {
    const response = await api.put(`/maps/${mapId}/markers/bulk`, { order })
    return response.data
  },

  /**
   * Import markers từ projects
   * @param {number} mapId
   * @param {Array} projectIds
   */
  async importFromProjects(mapId, projectIds) {
    const response = await api.post(`/maps/${mapId}/import-projects`, {
      project_ids: projectIds,
    })
    return response.data
  },

  // ============================================================
  // PUBLIC VIEW
  // ============================================================

  /**
   * Lấy map public theo share token (không cần auth)
   * @param {string} shareToken
   * @param {string} password - Mật khẩu (nếu có)
   */
  async getPublicMap(shareToken, password = null) {
    const params = password ? { password } : {}
    const response = await api.get(`/maps/view/${shareToken}`, { params })
    return response.data
  },

  /**
   * Lấy markers của map public với filter
   * @param {string} shareToken
   * @param {string} type - 'all', 'ban', 'thue'
   */
  async getPublicMapMarkers(shareToken, type = 'all') {
    const response = await api.get(`/maps/view/${shareToken}/markers`, {
      params: { type },
    })
    return response.data
  },

  /**
   * Lấy paths (shapes) của map public
   * @param {string} shareToken
   */
  async getPublicMapPaths(shareToken) {
    const response = await api.get(`/maps/view/${shareToken}/paths`)
    return response.data
  },

  // ============================================================
  // LAYERS & SHAPES (Drawing) for Property Maps
  // ============================================================

  /**
   * Lấy layers của property map
   * @param {number} mapId
   */
  async getLayers(mapId) {
    const response = await api.get(`/maps/${mapId}/layers`)
    return response.data
  },

  /**
   * Tạo layer mới cho property map
   * @param {number} mapId
   * @param {Object} data - { name, layer_type, ... }
   */
  async createLayer(mapId, data) {
    const response = await api.post(`/maps/${mapId}/layers`, data)
    return response.data
  },

  /**
   * Lấy shapes (paths) của property map
   * @param {number} mapId
   * @param {number} layerId - Optional filter by layer
   */
  async getShapes(mapId, layerId = null) {
    const params = layerId ? { layer_id: layerId } : {}
    const response = await api.get(`/maps/${mapId}/shapes`, { params })
    return response.data
  },

  /**
   * Tạo shape mới cho property map
   * @param {number} mapId
   * @param {Object} data - { layer_id, shape_type, geojson, ... }
   */
  async createShape(mapId, data) {
    const response = await api.post(`/maps/${mapId}/shapes`, data)
    return response.data
  },

  /**
   * Cập nhật shape
   * @param {number} mapId
   * @param {number} shapeId
   * @param {Object} data
   */
  async updateShape(mapId, shapeId, data) {
    const response = await api.put(`/maps/${mapId}/shapes/${shapeId}`, data)
    return response.data
  },

  /**
   * Xóa shape
   * @param {number} mapId
   * @param {number} shapeId
   */
  async deleteShape(mapId, shapeId) {
    const response = await api.delete(`/maps/${mapId}/shapes/${shapeId}`)
    return response.data
  },

  // ============================================================
  // SHARE & ANALYTICS
  // ============================================================

  /**
   * Ghi nhận share
   * @param {number} mapId
   * @param {Object} data - { method, recipient }
   */
  async recordShare(mapId, data) {
    const response = await api.post(`/maps/${mapId}/share`, data)
    return response.data
  },

  /**
   * Lấy analytics của map
   * @param {number} mapId
   */
  async getAnalytics(mapId) {
    const response = await api.get(`/maps/${mapId}/analytics`)
    return response.data
  },

  // ============================================================
  // TEMPLATES
  // ============================================================

  /**
   * Lấy danh sách marker templates
   */
  async getMarkerTemplates() {
    const response = await api.get('/maps/templates')
    return response.data
  },

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================

  /**
   * Tạo share URL
   * @param {string} shareToken
   */
  getShareUrl(shareToken) {
    return `${window.location.origin}/map/view/${shareToken}`
  },

  /**
   * Marker type options
   */
  getMarkerTypeOptions() {
    return [
      { value: 'custom', label: 'Tự do', color: '#10B981' },
      { value: 'ban', label: 'Bán', color: '#EF4444' },
      { value: 'thue', label: 'Cho thuê', color: '#3B82F6' },
      { value: 'both', label: 'Bán & Thuê', color: '#8B5CF6' },
    ]
  },

  /**
   * Property type options
   */
  getPropertyTypeOptions() {
    return [
      { value: 'can_ho', label: 'Căn hộ', icon: 'apartment' },
      { value: 'dat_nen', label: 'Đất nền', icon: 'land' },
      { value: 'biet_thu', label: 'Biệt thự', icon: 'villa' },
      { value: 'shophouse', label: 'Shophouse', icon: 'shop' },
      { value: 'nha_pho', label: 'Nhà phố', icon: 'house' },
      { value: 'condotel', label: 'Condotel', icon: 'hotel' },
    ]
  },

  /**
   * Marker color presets
   */
  getMarkerColors() {
    return [
      { name: 'Đỏ', value: '#EF4444' },
      { name: 'Xanh dương', value: '#3B82F6' },
      { name: 'Xanh lá', value: '#10B981' },
      { name: 'Tím', value: '#8B5CF6' },
      { name: 'Cam', value: '#F59E0B' },
      { name: 'Hồng', value: '#EC4899' },
      { name: 'Xanh cyan', value: '#06B6D4' },
      { name: 'Xám', value: '#6B7280' },
    ]
  },

  /**
   * Map style options
   */
  getMapStyleOptions() {
    return [
      { value: 'roadmap', label: 'Đường phố' },
      { value: 'satellite', label: 'Vệ tinh' },
      { value: 'terrain', label: 'Địa hình' },
      { value: 'hybrid', label: 'Kết hợp' },
    ]
  },

  /**
   * Đà Nẵng default center
   */
  getDefaultCenter() {
    return {
      lat: 16.0544,
      lng: 108.2022,
      zoom: 13,
    }
  },

  /**
   * Format giá hiển thị
   * @param {number} value - Giá trị (VNĐ)
   * @param {string} unit - 'ty', 'trieu', 'trieu_m2'
   */
  formatPrice(value, unit = 'ty') {
    if (!value) return ''

    if (unit === 'trieu_m2') {
      return `${(value / 1e6).toFixed(0)} triệu/m²`
    } else if (unit === 'trieu') {
      return `${(value / 1e6).toFixed(0)} triệu`
    } else {
      // ty
      const ty = value / 1e9
      if (ty >= 1) {
        return `${ty.toFixed(1)} tỷ`
      } else {
        return `${(value / 1e6).toFixed(0)} triệu`
      }
    }
  },

  /**
   * Format giá thuê
   * @param {number} value
   * @param {string} unit - 'thang', 'nam'
   */
  formatRentPrice(value, unit = 'thang') {
    if (!value) return ''

    const trieu = value / 1e6
    const suffix = unit === 'nam' ? '/năm' : '/tháng'
    return `${trieu.toFixed(0)} triệu${suffix}`
  },
}

export default mapService
